import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
} from '@material-ui/core';
import { Api } from 'api';
import { formatToSloDate } from 'pages/Sessions/SessionsPage';
import { useState, useEffect } from 'react';

interface Transaction {
  id: string;
  createdAt: string;
  reason: string;
  reference?: string;
  amount: { formatted: string; value: number };
  balance: { formatted: string };
  note?: string;
}

interface ReservationReleaseModalProps {
  open: boolean;
  walletId: string;
  ownerName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const ReservationReleaseModal = ({
  open,
  walletId,
  ownerName,
  onClose,
  onSuccess,
}: ReservationReleaseModalProps) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [releasingRef, setReleasingRef] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadTransactions = () => {
    setLoading(true);
    setError(null);
    Api.billing
      .listTransactionsForWallet(walletId)
      .then((response) => {
        const data = response.data?.data || [];
        // Filter to show negative balance entries (reservation, charge, payment)
        // Only entries with a reference can be released
        const negativeEntries = data.filter((t: Transaction) => t.amount.value < 0 && t.reference);
        setTransactions(negativeEntries);
      })
      .catch((err) => {
        setError(err?.response?.data?.error?.message || 'Failed to load transactions');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (open && walletId) {
      loadTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, walletId]);

  const handleRelease = async (reference: string) => {
    setReleasingRef(reference);
    try {
      const response = await Api.billing.releaseReservation(reference);
      const result = response.data;
      if (result.success) {
        alert(result.message);
        onSuccess();
        loadTransactions(); // Reload to update the list
      } else {
        alert(result.message || 'Failed to release reservation');
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.error?.message || 'Failed to release reservation';
      alert(errorMessage);
    } finally {
      setReleasingRef(null);
    }
  };

  const handleClose = () => {
    if (!releasingRef) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Release Reserved Funds - {ownerName}</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : transactions.length === 0 ? (
          <Typography>No pending reservations found for this wallet.</Typography>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              Select an entry to release the funds back to the wallet. Only 'reservation' type
              entries can be released.
            </Typography>
            <TableContainer component={Paper} style={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((transaction) => {
                    const isReservation = transaction.reason === 'reservation';
                    return (
                      <TableRow key={transaction.id} hover>
                        <TableCell>{formatToSloDate(new Date(transaction.createdAt))}</TableCell>
                        <TableCell style={{ textTransform: 'capitalize' }}>
                          {transaction.reason?.replace(/_/g, ' ')}
                        </TableCell>
                        <TableCell>{transaction.reference || '-'}</TableCell>
                        <TableCell align="right">{transaction.amount.formatted}</TableCell>
                        <TableCell align="center">
                          {isReservation ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              disabled={releasingRef !== null}
                              onClick={() =>
                                transaction.reference && handleRelease(transaction.reference)
                              }
                            >
                              {releasingRef === transaction.reference ? 'Releasing...' : 'Release'}
                            </Button>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={releasingRef !== null}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
